import practicalTestService from 'ApiService/Teacher/practicalTestService';
import testService from 'ApiService/Teacher/TestService';
import Card from 'components/card';
import { AuthContext } from 'contexts/AuthContext';
import ConfirmationModal from 'pages/Common/ConfirmationModal';
import SelectClass from 'pages/Teacher/Attendance/Report/ReportAttendance/AttendancesReportClass';
import { useContext, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { UseContextProvider } from 'views/admin/main/users/newUser/contexts/StepperContext';

const Index = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [classData, setClassData] = useState({ courseId: '', batchId: '' });
  const [testData, setTestData] = useState({
    title: '',
    description: '',
    contentType: 'typed',
    startDate: '',
    startTime: '',
    endDate: '',
    endTime: '',
  });
  const [files, setFiles] = useState([]);
  const [disable, setDisable] = useState(true);
  const [loading, setLoading] = useState(false);
  const [courses, setCourses] = useState([]);
  const [loadingCourses, setLoadingCourses] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const router = useNavigate();
  const { state } = useContext(AuthContext);

  const steps = [
    { stepNo: 1, name: 'Class Info' },
    { stepNo: 2, name: 'Test Details' },
  ];

  const fetchCourses = async () => {
    setLoadingCourses(true);
    try {
      const response = await testService.getAllCourses();
      if (response.data.success) {
        setCourses(response.data.courses);
      } else {
        toast.error('Failed to fetch courses.');
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to fetch courses.');
    } finally {
      setLoadingCourses(false);
    }
  };

  useEffect(() => {
    fetchCourses();
  }, []);

  useEffect(() => {
    if (currentStep === 2) {
      const { title, startDate, startTime, endDate, endTime } = testData;
      if (!classData.batchId) {
        setDisable(true);
      } else if (!title || !startDate || !startTime || !endDate || !endTime) {
        setDisable(true);
      } else if (testData.contentType !== 'typed' && files.length === 0) {
        setDisable(true);
      } else {
        setDisable(false);
      }
    }
  }, [testData, files, currentStep, classData.batchId]);

  const handleConfirm = () => {
    if (!classData.batchId) {
      toast.error('Please select a batch first.');
      return;
    }
    setIsModalOpen(true);
  };

  const handleSubmit = async () => {
    if (loading) return;
    if (!classData.batchId) {
      toast.error('Batch is required.');
      return;
    }
    try {
      setLoading(true);
      const formData = new FormData();
      formData.append('title', testData.title);
      formData.append('description', testData.description);
      formData.append('contentType', testData.contentType);
      formData.append('batch', classData.batchId);
      formData.append('course', classData.courseId);
      formData.append('startDate', testData.startDate);
      formData.append('startTime', testData.startTime);
      formData.append('endDate', testData.endDate);
      formData.append('endTime', testData.endTime);
      if (files.length > 0) {
        files.forEach((f) => formData.append('testFiles', f));
      }

      const response = await practicalTestService.createPracticalTest(formData);
      if (response.data.success) {
        toast.success('Practical test created successfully!');
        setCurrentStep(1);
        setIsModalOpen(false);
        setTestData({
          title: '',
          description: '',
          contentType: 'typed',
          startDate: '',
          startTime: '',
          endDate: '',
          endTime: '',
        });
        setFiles([]);
        router('/teacher/view/tests');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Error creating practical test.';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleNext = () => {
    if (!disable) setCurrentStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setCurrentStep((prev) => (prev > 1 ? prev - 1 : prev));
  };

  const displayStep = (step) => {
    switch (step.stepNo) {
      case 1:
        return (
          <SelectClass
            setLoading={setLoading}
            setDisable={setDisable}
            setClassData={setClassData}
            classData={classData}
            courses={courses}
            loadingCourses={loadingCourses}
          />
        );
      case 2:
        return (
          <div className="p-6">
            {classData.batchId && (
              <div className="mb-4 flex items-center gap-2 rounded-lg bg-green-50 px-4 py-2 text-sm text-green-700 dark:bg-green-900/20 dark:text-green-400">
                <span className="font-medium">Batch selected:</span>
                <span>{classData.batchId}</span>
                <span className="ml-auto rounded-full bg-green-200 px-2 py-0.5 text-xs font-medium text-green-800 dark:bg-green-800 dark:text-green-200">
                  Ready
                </span>
              </div>
            )}
            <div className="mb-4">
              <label className="mb-2 block text-sm font-medium text-navy-700 dark:text-white">
                Title <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={testData.title}
                onChange={(e) => setTestData({ ...testData, title: e.target.value })}
                placeholder="Enter test title"
                className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm outline-none focus:border-brand-400 dark:border-navy-600 dark:bg-navy-800 dark:text-white"
              />
            </div>

            <div className="mb-4">
              <label className="mb-2 block text-sm font-medium text-navy-700 dark:text-white">
                Content Type
              </label>
              <select
                value={testData.contentType}
                onChange={(e) => setTestData({ ...testData, contentType: e.target.value })}
                className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm outline-none focus:border-brand-400 dark:border-navy-600 dark:bg-navy-800 dark:text-white"
              >
                <option value="typed">Type Question/Description</option>
                <option value="file">Upload File(s)</option>
                <option value="txt">Upload Text File (.txt)</option>
              </select>
            </div>

            {testData.contentType === 'typed' && (
              <div className="mb-4">
                <label className="mb-2 block text-sm font-medium text-navy-700 dark:text-white">
                  Description / Question
                </label>
                <textarea
                  value={testData.description}
                  onChange={(e) => setTestData({ ...testData, description: e.target.value })}
                  placeholder="Type the test question or description here..."
                  rows="6"
                  className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm outline-none focus:border-brand-400 dark:border-navy-600 dark:bg-navy-800 dark:text-white"
                />
              </div>
            )}

            {(testData.contentType === 'file' || testData.contentType === 'txt') && (
              <div className="mb-4">
                <label className="mb-2 block text-sm font-medium text-navy-700 dark:text-white">
                  Upload File(s) <span className="text-red-500">*</span>
                </label>
                <input
                  type="file"
                  multiple
                  onChange={(e) => setFiles(Array.from(e.target.files))}
                  className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm outline-none focus:border-brand-400 dark:border-navy-600 dark:bg-navy-800 dark:text-white"
                />
                {files.length > 0 && (
                  <ul className="mt-2 space-y-1">
                    {files.map((f, i) => (
                      <li key={i} className="text-xs text-green-600">{f.name}</li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            <div className="mb-4 grid grid-cols-2 gap-4">
              <div>
                <label className="mb-2 block text-sm font-medium text-navy-700 dark:text-white">
                  Start Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={testData.startDate}
                  onChange={(e) => setTestData({ ...testData, startDate: e.target.value })}
                  className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm outline-none focus:border-brand-400 dark:border-navy-600 dark:bg-navy-800 dark:text-white"
                />
              </div>
              <div>
                <label className="mb-2 block text-sm font-medium text-navy-700 dark:text-white">
                  Start Time <span className="text-red-500">*</span>
                </label>
                <input
                  type="time"
                  value={testData.startTime}
                  onChange={(e) => setTestData({ ...testData, startTime: e.target.value })}
                  className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm outline-none focus:border-brand-400 dark:border-navy-600 dark:bg-navy-800 dark:text-white"
                />
              </div>
            </div>

            <div className="mb-4 grid grid-cols-2 gap-4">
              <div>
                <label className="mb-2 block text-sm font-medium text-navy-700 dark:text-white">
                  End Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={testData.endDate}
                  onChange={(e) => setTestData({ ...testData, endDate: e.target.value })}
                  className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm outline-none focus:border-brand-400 dark:border-navy-600 dark:bg-navy-800 dark:text-white"
                />
              </div>
              <div>
                <label className="mb-2 block text-sm font-medium text-navy-700 dark:text-white">
                  End Time <span className="text-red-500">*</span>
                </label>
                <input
                  type="time"
                  value={testData.endTime}
                  onChange={(e) => setTestData({ ...testData, endTime: e.target.value })}
                  className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm outline-none focus:border-brand-400 dark:border-navy-600 dark:bg-navy-800 dark:text-white"
                />
              </div>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="mt-3 h-full w-full">
      <div className="h-[350px] w-full rounded-[20px] bg-gradient-to-br from-brand-400 to-brand-600 md:h-[390px]" />
      <div className={`w-md:2/3 mx-auto h-full ${currentStep === 1 ? 'w-5/6' : 'w-6/6'} md:px-3 3xl:w-7/12`}>
        <Card extra="-mt-[280px] w-full md:-mt-[340px] md:px-[70px] h-full mx-auto pb-3">
          <div className="w-full rounded-[20px]">
            <UseContextProvider>
              {displayStep(steps[currentStep - 1])}
            </UseContextProvider>
          </div>
          <div className="flex flex-col justify-between px-3 pt-2 md:flex-row md:px-8">
            {currentStep > 1 && (
              <button
                onClick={handleBack}
                className="mb-3 cursor-pointer rounded-xl bg-lightPrimary px-16 py-2 font-medium text-navy-700 transition duration-200 dark:bg-white/5 dark:text-white dark:hover:bg-white/10 dark:active:bg-white/20 md:mb-0"
              >
                Prev
              </button>
            )}
            <div className="ml-auto">
              {currentStep < steps.length ? (
                <button
                  disabled={disable}
                  onClick={handleNext}
                  className="linear cursor-pointer rounded-xl bg-brand-900 px-16 py-2 font-medium text-white transition duration-200 hover:bg-brand-800 active:bg-brand-700 dark:bg-brand-400 dark:hover:bg-brand-300 dark:active:opacity-90"
                >
                  Next
                </button>
              ) : (
                <button
                  disabled={disable}
                  onClick={handleConfirm}
                  className="linear cursor-pointer rounded-xl bg-brand-900 px-16 py-2 font-medium text-white transition duration-200 hover:bg-brand-800 active:bg-brand-700 dark:bg-brand-400 dark:hover:bg-brand-300 dark:active:opacity-90"
                >
                  {loading ? 'Loading...' : 'Submit'}
                </button>
              )}
            </div>
          </div>
        </Card>
      </div>
      <ConfirmationModal
        isOpen={isModalOpen}
        onClose={() => {
          if (!loading) setIsModalOpen(false);
        }}
        onConfirm={handleSubmit}
        title="Confirm Practical Test"
        message="Are you sure you want to create this practical test for the selected class?"
        confirmText={loading ? 'Processing...' : 'Yes, Create Test'}
        cancelText="Cancel"
        icon="info"
      />
    </div>
  );
};

export default Index;
